import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { RefreshCw, Send, Mail, CheckCircle2, AlertTriangle, Eye, CalendarClock } from "lucide-react";

const fmt = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"; } catch { return "—"; } };

const PREVIEWS = [
  { key: "entity_profile", label: "Rappel fiche club/asso" },
  { key: "entity_recap_winner", label: "Récap mensuel — actu gagnante" },
  { key: "entity_recap_encourage", label: "Récap mensuel — encouragement" },
  { key: "fiche_incomplete", label: "Rappel fiche commerçant" },
  { key: "trial_offer", label: "Rappel 1re offre offerte" },
  { key: "pro_recap", label: "Récap mensuel commerçant" },
];

export default function AdminEntityReminders() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/entities/reminder-stats"); setData(data); }
    catch { setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const run = async (url, okMsg) => {
    setBusy(url);
    try {
      const { data } = await api.post(url);
      if (data?.already_sent) { toast.info("Récap déjà envoyé ce mois-ci — aucun nouvel envoi."); return; }
      const extra = data?.entity_profile_sent != null
        ? ` : ${data.entity_profile_sent} envoyé(s), ${data.entity_profile_skipped} en attente, ${data.entity_profile_failed} échec(s)`
        : "";
      toast.success(okMsg + extra);
      await load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
    finally { setBusy(""); }
  };

  const openPreview = async (p) => {
    try {
      const { data } = await api.get("/admin/email-preview", { params: { key: p.key } });
      setPreviewHtml(data); setPreviewTitle(p.label);
    } catch { toast.error("Aperçu indisponible."); }
  };

  const rows = data?.items || [];

  return (
    <div data-testid="admin-entity-reminders">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Relances clubs & associations</h1>
          <p className="text-sm text-slate-500 mt-1">Suivi des fiches incomplètes, relances profil (J+0/J+1/J+3/J+7 puis mensuel) et récaps mensuels. Aperçu et déclenchement à la demande.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="entity-rem-reload">
          <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-2xl font-bold text-slate-900">{data?.total ?? "—"}</div><div className="text-xs text-slate-500">Fiches suivies</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-2xl font-bold text-amber-600">{data?.incomplete ?? "—"}</div><div className="text-xs text-slate-500">Encore incomplètes</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-2xl font-bold text-emerald-600">{data?.completed_after ?? "—"}</div><div className="text-xs text-slate-500">Complétées après relance</div></div>
        <div className="bg-white border border-emerald-200 rounded-xl p-4" data-testid="entity-rem-rate">
          <div className="text-2xl font-bold text-emerald-700">{data ? `${data.rate}%` : "—"}</div>
          <div className="text-xs text-slate-500">Taux de complétion</div>
          <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${data ? data.rate : 0}%` }} />
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={() => run("/admin/entities/profile-reminder", "Relances profil déclenchées.")} disabled={busy !== ""} className="btn-shine text-white" data-testid="entity-rem-send-profile">
          <Send size={14} className={`mr-1.5 ${busy.includes("profile-reminder") ? "animate-pulse" : ""}`} /> Envoyer les rappels fiche
        </Button>
        <Button variant="outline" onClick={() => run("/admin/entities/monthly-recap-run", "Récap mensuel clubs/assos lancé.")} disabled={busy !== ""} data-testid="entity-rem-run-entity-recap">
          <CalendarClock size={14} className="mr-1.5" /> Lancer le récap mensuel clubs/assos
        </Button>
        <Button variant="outline" onClick={() => run("/admin/pro/monthly-recap-run", "Récap mensuel commerçants lancé.")} disabled={busy !== ""} data-testid="entity-rem-run-pro-recap">
          <CalendarClock size={14} className="mr-1.5" /> Lancer le récap mensuel commerçants
        </Button>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 font-semibold text-slate-800 text-sm mb-3"><Mail size={16} className="text-violet-600" /> Aperçu des e-mails</div>
        <div className="flex flex-wrap gap-2">
          {PREVIEWS.map((p) => (
            <Button key={p.key} variant="outline" size="sm" onClick={() => openPreview(p)} data-testid={`entity-rem-preview-${p.key}`}>
              <Eye size={13} className="mr-1.5" /> {p.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Fiche</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Ville</th>
                <th className="text-center px-4 py-3">Complétude</th>
                <th className="text-center px-4 py-3">Relances</th>
                <th className="text-left px-4 py-3">Dernier envoi</th>
                <th className="text-left px-4 py-3">Éléments manquants</th>
                <th className="text-left px-4 py-3">État</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Chargement…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Aucune fiche suivie.</td></tr>
              ) : rows.map((r) => (
                <tr key={`${r.kind}-${r.id}`} className="border-t border-slate-100" data-testid={`entity-rem-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{r.name || "—"}</div>
                    <div className="text-xs text-slate-400">{r.email || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.kind === "club" ? "Club" : "Association"}</td>
                  <td className="px-4 py-3 text-slate-600">{r.city || "—"}</td>
                  <td className="px-4 py-3 text-center font-semibold">{r.pct}%</td>
                  <td className="px-4 py-3 text-center font-semibold">{r.reminder_count}</td>
                  <td className="px-4 py-3 text-slate-600">{fmt(r.last_sent)}</td>
                  <td className="px-4 py-3">
                    {r.missing.length === 0 ? <span className="text-xs text-slate-400">—</span> :
                      <div className="flex flex-wrap gap-1">{r.missing.map((m) => <span key={m} className="text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-600 border border-red-100">{m}</span>)}</div>}
                  </td>
                  <td className="px-4 py-3">
                    {r.complete
                      ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 size={14} /> Complète</span>
                      : <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600"><AlertTriangle size={14} /> Incomplète</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!previewHtml} onOpenChange={(o) => !o && setPreviewHtml(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden" data-testid="entity-rem-preview-dialog">
          <DialogHeader><DialogTitle>Aperçu — {previewTitle}</DialogTitle></DialogHeader>
          <iframe title="preview" srcDoc={previewHtml || ""} className="w-full h-[70vh] border border-slate-200 rounded-lg bg-white" data-testid="entity-rem-preview-frame" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
